import React, { useEffect, useRef } from "react";

const LoadingCanvas = ({ imageSrc, onComplete, onCompleteSpin, progress }) => {
  const canvasRef = useRef(null);
  const hasStartedRef = useRef(false);
  const progressRef = useRef(0);

  // Keep ref in sync with prop
  useEffect(() => {
    progressRef.current = progress;
  }, [progress]);

  useEffect(() => {
    // 🔒 Prevent double start (React strict mode)
    if (hasStartedRef.current) return;
    hasStartedRef.current = true;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    // =============================
    // Canvas setup (DPR-safe)
    // =============================
    const dpr = window.devicePixelRatio || 1;
    const width = window.innerWidth;
    const height = window.innerHeight;

    canvas.width = width * dpr;
    canvas.height = height * dpr;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;

    // Draw in CSS pixels
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    // =============================
    // VIDEO loading (replaces GIF)
    // =============================
    const video = document.createElement("video");
    let videoReady = false;

    video.src = imageSrc; // MUST be .mp4 or .webm
    video.loop = true;
    video.muted = true; // required for autoplay
    video.playsInline = true;
    video.preload = "auto";

    video.onloadeddata = () => {
      videoReady = true;
      video.play().catch(() => {});
    };

    // =============================
    // Phases
    // =============================
    let phase = "gathering";
    let startTime = null;
    let rafId;

    // =============================
    // Spin config
    // =============================
    const spinTotalTime = 2500;
    const spinCycleDuration = 700;
    const spinSpinTime = 500;

    // =============================
    // Pixel dissolve config (DPR aware)
    // =============================
    const dissolvePixelSize = 10 * dpr;
    const blocksPerFrame = width < 768 ? 120 : 250;

    let dissolveImageData;
    let blockOrder;
    let blockCursor = 0;
    let blockCols;

    const initPixelDissolve = () => {
      dissolveImageData = ctx.getImageData(
        0,
        0,
        width * dpr,
        height * dpr
      );

      blockCols = Math.ceil((width * dpr) / dissolvePixelSize);
      const blockRows = Math.ceil((height * dpr) / dissolvePixelSize);
      const totalBlocks = blockCols * blockRows;

      blockOrder = new Uint32Array(totalBlocks);
      for (let i = 0; i < totalBlocks; i++) blockOrder[i] = i;

      // Shuffle
      for (let i = totalBlocks - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [blockOrder[i], blockOrder[j]] = [blockOrder[j], blockOrder[i]];
      }

      blockCursor = 0;
    };

    // =============================
    // Animation loop
    // =============================
    const animate = (timestamp) => {
      ctx.clearRect(0, 0, width, height);
      ctx.fillStyle = "#000117";
      ctx.fillRect(0, 0, width, height);

      const cx = width / 2;
      const cy = height / 2;

      // -------- GATHERING --------
      if (phase === "gathering") {
        const loadProgress = Math.min(Math.max(progressRef.current, 0), 1);
        const percent = Math.round(loadProgress * 100);

        ctx.fillStyle = "#ffffff";
        ctx.font = "28px sans-serif";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText("Gathering Assets...", cx, cy - 40);

        // Progress bar
        const bw = 360;
        const bh = 16;
        const x = cx - bw / 2;
        const y = cy;

        ctx.lineWidth = 2;
        ctx.strokeStyle = "#ffffff";
        ctx.strokeRect(x, y, bw, bh);

        ctx.fillStyle = "#1a1a1a";
        ctx.fillRect(x + 1, y + 1, bw - 2, bh - 2);

        ctx.fillStyle = "#ce41c4";
        ctx.fillRect(x + 1, y + 1, (bw - 2) * loadProgress, bh - 2);

        ctx.fillStyle = "#ffffff";
        ctx.font = "14px sans-serif";
        ctx.fillText(`${percent}%`, cx, y + bh / 2);

        if (loadProgress >= 1) {

          if (videoReady) {
            phase = "spinning";
            startTime = null;
          }
        }

      }

      // -------- SPINNING --------
      else if (phase === "spinning") {
          if (!startTime) startTime = timestamp;
          const elapsed = timestamp - startTime;

          onCompleteSpin?.();

          const cycle = elapsed % spinCycleDuration;
          const angle =
            cycle < spinSpinTime
              ? (cycle / spinSpinTime) * Math.PI * 2
              : 0;

          const size = Math.min(width, height) / 2; 
          const imgX = cx - size / 2; // horizontal center
          const imgY = cy - size / 2; // vertical center

          ctx.save();
          ctx.translate(cx, cy);          // move origin to center
          ctx.drawImage(video, -size / 2, -size / 2, size, size);
          ctx.restore();

          if (elapsed >= spinTotalTime) {
            phase = "dissolving";
            initPixelDissolve();
          }
      }


      // -------- DISSOLVE --------
      else if (phase === "dissolving") {
        const data = dissolveImageData.data;
        const deviceWidth = width * dpr;

        for (
          let i = 0;
          i < blocksPerFrame && blockCursor < blockOrder.length;
          i++, blockCursor++
        ) {
          const index = blockOrder[blockCursor];
          const bx = (index % blockCols) * dissolvePixelSize;
          const by = Math.floor(index / blockCols) * dissolvePixelSize;

          for (let y = 0; y < dissolvePixelSize; y++) {
            for (let x = 0; x < dissolvePixelSize; x++) {
              const px = bx + x;
              const py = by + y;
              if (px >= deviceWidth || py >= height * dpr) continue;

              const idx = (py * deviceWidth + px) * 4;
              data[idx + 3] = 0;
            }
          }
        }

        ctx.putImageData(dissolveImageData, 0, 0);

        if (blockCursor >= blockOrder.length) {
          video.pause();
          onComplete?.();
          return;
        }
      }

      rafId = requestAnimationFrame(animate);
    };

    rafId = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(rafId);
      video.pause();
      video.src = "";
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
      }}
    />
  );
};

export default LoadingCanvas;
